import { Button, FlexGrid, Image, Panel, Text } from '@gasbuddy/react-components';
import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { hasValue, localyticsEvent, noop, toReadableMonthYear } from '../../../lib/utils';
import { ANALYTICS_EVENTS, DEFAULT_LOGO_URL } from '../../constants';
import { options } from '../../constants/reviewRatings';
import { Brand } from '../../prop-types/brand';
import styles from './ReviewItem.module.css';

const cx = classnames.bind(styles);

export default function ReviewItem({
  review,
  brands,
  fetchBrands,
  showReplies,
  isAnonUser,
  onFlagReview,
  interactive,
  updateReview,
}) {
  const [errorMessage, setErrorMessage] = useState(review.updateError);

  const redirectAnonUser = useCallback(() => {
    window.location.href = '/login';
  }, []);

  const handleFlagReview = useCallback(() => {
    localyticsEvent(ANALYTICS_EVENTS.REVIEW_FLAG_CLICKED, { isLoggedIn: !isAnonUser });

    if (isAnonUser) {
      redirectAnonUser();
      return;
    }

    onFlagReview(review.id);
  }, [isAnonUser, redirectAnonUser, onFlagReview, review.id]);


  const handleAgreeWithReview = useCallback(() => {
    localyticsEvent(ANALYTICS_EVENTS.REVIEW_AGREED, { isLoggedIn: !isAnonUser });

    if (isAnonUser) {
      redirectAnonUser();
      return;
    }

    updateReview({
      id: review.id,
      hasAgreed: true,
    });
  }, [isAnonUser, redirectAnonUser, updateReview, review.id]);

  const handleUnagreeWithReview = useCallback(() => {
    // since only logged in users can unagree review, adding context whether user is logged in or not isnt needed
    localyticsEvent(ANALYTICS_EVENTS.REVIEW_UNAGREED);

    updateReview({
      id: review.id,
      hasAgreed: false,
    });
  }, [updateReview, review.id]);

  useEffect(() => {
    if (!hasValue(brands)) {
      const brandIds = review.replies.map(reply => reply.brandId);
      fetchBrands(brandIds);
    }
  }, [review, brands, fetchBrands]);

  useEffect(() => {
    if (review.updateError) {
      setErrorMessage(review.updateError);
    }
  }, [review.updateError]);

  const reviewRating = review.rating < 1 ? 1 : review.rating; // safe non 0 ratings
  const formattedReviewDate = toReadableMonthYear(review.date);
  const reviewRatingOption = options.find(option => option.value === reviewRating);

  return (
    <Panel bordered className={cx('reviewItemPanel')} color="white" rounded>
      <FlexGrid>
        <FlexGrid.Column mobile={8} tablet={10} className={cx('headerContainer')}>
          <div className={cx('inlineContainer')}>
            <Text as="a" color="midnight" href={`https://www.gasbuddy.com/member/${review.user.name.toLowerCase()}`}>
              <Image
                alt={`${review.user.name}'s avatar image.`}
                circular
                className={cx('iconSizing')}
                src={review.user.avatarUrl}
              />
            </Text>
          </div>
          <div className={cx('titleContainer')}>
            <Text as="a" color="midnight" href={`https://www.gasbuddy.com/member/${review.user.name.toLowerCase()}`}>
              <Text bold color="midnight">{review.user.name}</Text>
            </Text>
            <br />
            <Text className={cx('date')}>{formattedReviewDate}</Text>
          </div>
        </FlexGrid.Column>
        <FlexGrid.Column mobile={4} tablet={2} className={cx('ratingContainer')}>
          <Image
            alt={`${reviewRatingOption.label} Review Icon`}
            className={cx('iconSizing')}
            src={reviewRatingOption.icon}
          />
        </FlexGrid.Column>
        <FlexGrid.Column>
          <Text as="p" className={cx('content')}>{review.content}</Text>
        </FlexGrid.Column>
        {interactive && (
          <React.Fragment>
            <FlexGrid.Column mobile={8} tablet={10}>
              {(isAnonUser || review.isReportable) && (
                <Button link className={cx('flagButton')} onClick={handleFlagReview}>
                  Flag as inappropriate
                </Button>
              )}
              {!isAnonUser && !review.hasAgreed && !review.isReportable && (
                <Text className={cx('flagged')}>Flagged as inappropriate!</Text>
              )}
            </FlexGrid.Column>
            <FlexGrid.Column mobile={4} tablet={2} style={{ textAlign: 'right' }}>
              <Button
                size="sm"
                className={cx('agreeButton', { agreed: review.hasAgreed })}
                onClick={review.hasAgreed ? handleUnagreeWithReview : handleAgreeWithReview}
                loading={review.isUpdating}
                disabled={review.isUpdating || (!isAnonUser && !review.hasAgreed && !review.isReportable)}
              >
                <Image
                  src={review.hasAgreed ? '//static.gasbuddy.com/web/icons/thumbsup-active.svg' : '//static.gasbuddy.com/web/icons/thumbsup.svg'}
                  alt="Agree Icon"
                  className={cx('agreeIcon')}
                />
                &nbsp;&nbsp;
                {Number(review.agreeTotal) > 0 ? `${review.agreeTotal} Agree` : 'Agree ?'}
              </Button>
            </FlexGrid.Column>
            {!!errorMessage && (
              <FlexGrid.Column>
                <Text as="p" color="orange">{errorMessage}</Text>
              </FlexGrid.Column>
            )}
          </React.Fragment>
        )}
        {showReplies && review.replies.length > 0 && review.replies.map((reply) => {
          const formattedReplyDate = toReadableMonthYear(reply.createDate);
          return (
            <FlexGrid.Column mobile={10} mobileOffset={2} key={reply.id}>
              <div className={cx('replyContainer')}>
                <div className={cx('headerContainer')}>
                  <Image
                    src={(brands[reply.brandId] && brands[reply.brandId].image_url) || DEFAULT_LOGO_URL}
                    className={cx('replyIcon')}
                    alt={`${(brands[reply.brandId] && brands[reply.brandId].name) || ''} corporate logo.`}
                    circular
                    bordered
                  />
                  <div className={cx('titleContainer')}>
                    <Text bold color="midnight" className={cx('replyIcon')}>
                      {`Reply from ${reply.authorName}`}
                    </Text>
                    <br />
                    <Text className={cx('date')}>{formattedReplyDate}</Text>
                  </div>
                </div>
                <Text as="p" className={cx('content')}>{reply.text}</Text>
              </div>
            </FlexGrid.Column>
          );
        })}
      </FlexGrid>
    </Panel>
  );
}

ReviewItem.propTypes = {
  brands: PropTypes.objectOf(Brand),
  fetchBrands: PropTypes.func,
  review: PropTypes.shape({
    id: PropTypes.number,
    date: PropTypes.string,
    content: PropTypes.string,
    user: PropTypes.shape({
      avatarUrl: PropTypes.string,
      name: PropTypes.string.isRequired,
    }).isRequired,
    replies: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        createDate: PropTypes.string,
        brandId: PropTypes.number,
        authorName: PropTypes.string,
        text: PropTypes.string,
      }),
    ),
    updateError: PropTypes.string,
    rating: PropTypes.number,
    isReportable: PropTypes.bool,
    hasAgreed: PropTypes.bool,
    isUpdating: PropTypes.bool,
    agreeTotal: PropTypes.number,
  }).isRequired,
  showReplies: PropTypes.bool,
  isAnonUser: PropTypes.bool,
  onFlagReview: PropTypes.func,
  interactive: PropTypes.bool,
  updateReview: PropTypes.func,
};

ReviewItem.defaultProps = {
  brands: {},
  fetchBrands: noop,
  showReplies: false,
  onFlagReview: noop,
  isAnonUser: true,
  interactive: false,
  updateReview: noop,
};
